import { jsx, jsxs } from 'react/jsx-runtime';
import { DialogProvider } from '@lemonade-hq/cdk';
import { DE_REGION } from '@lemonade-hq/lemonation';
import { reddishTheme, pinkishTheme, ThemeProvider } from '@lemonade-hq/nuis';
import { RealtimeProvider } from '@lemonade-hq/realtime';
import React, { useMemo } from 'react';
import { AnalyticsProvider } from '../../providers/AnalyticsProvider.esm.js';
import { AuAProvider } from '../../providers/AuAProvider.esm.js';
import { ClientProvider } from '../../providers/ClientProvider.esm.js';
import { I18nProvider } from '../../providers/I18nProvider.esm.js';
import { InfraProvider } from '../../providers/InfraProvider.esm.js';
import { LayoutProvider } from '../../providers/LayoutProvider.esm.js';
import { MonitoringProvider } from '../../providers/MonitoringProvider.esm.js';
import { NavProvider } from '../../providers/NavProvider.esm.js';
import { UserProvider } from '../../providers/UserProvider.esm.js';
import { VisitorProvider } from '../../providers/VisitorProvider.esm.js';
import { LoginIframe } from '../LoginIframe/LoginIframe.esm.js';
import { LemonadeAppUiContainer } from './components/LemonadeAppUiContainer.esm.js';
import { useSetupTracking } from '../../hooks/useSetupTracking.esm.js';
import { useSetupCsrf } from '../../hooks/useSetupCsrf.esm.js';
import { useSetupClientAuth } from '../../hooks/useSetupClientAuth.esm.js';
import { ComplianceProvider } from '../../providers/ComplianceProvider.esm.js';

/* eslint-disable @typescript-eslint/naming-convention */ /* eslint-disable no-extra-boolean-cast */ function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
// MEMOS ********************************************
var MemoizedAnalyticsProvider = /*#__PURE__*/ React.memo(AnalyticsProvider);
var MemoizedNavProvider = /*#__PURE__*/ React.memo(NavProvider);
var MemoizedDialogProvider = /*#__PURE__*/ React.memo(DialogProvider);
var MemoizedAuAProvider = /*#__PURE__*/ React.memo(AuAProvider);
var MemoizedThemeProvider = /*#__PURE__*/ React.memo(ThemeProvider);
var MemoizedUserProvider = /*#__PURE__*/ React.memo(UserProvider);
var MemoizedVisitorProvider = /*#__PURE__*/ React.memo(VisitorProvider);
var MemoizedLayoutProvider = /*#__PURE__*/ React.memo(LayoutProvider);
var MemoizedInfraProvider = /*#__PURE__*/ React.memo(InfraProvider);
var MemoizedRealtimeProvider = /*#__PURE__*/ React.memo(RealtimeProvider);
var MemoizedMonitoringProvider = /*#__PURE__*/ React.memo(MonitoringProvider);
var MemoizedClientProvider = /*#__PURE__*/ React.memo(ClientProvider);
var MemoizedComplianceProvider = /*#__PURE__*/ React.memo(ComplianceProvider);
var LemonadeApp = function(props) {
    var locale = props.locale, analytics = props.analytics, realtime = props.realtime, csrf = props.security.csrf, nav = props.nav, monitoring = props.monitoring, auth = props.auth, user = props.user, infra = props.infra, _props_ssr = props.ssr, ssr = _props_ssr === void 0 ? false : _props_ssr, layout = props.layout, children = props.children;
    useSetupTracking({
        locale: locale,
        trackQuery: analytics.trackQuery,
        user: user,
        withPrepareTracking: analytics.withPrepareTracking
    });
    useSetupCsrf({
        csrf: csrf
    });
    var _useSetupClientAuth = useSetupClientAuth({
        auth: auth
    }), setupDone = _useSetupClientAuth.setupDone, loginIframeState = _useSetupClientAuth.loginIframeState;
    var themeName = useMemo(function() {
        return (locale === null || locale === void 0 ? void 0 : locale.region) === DE_REGION ? reddishTheme.name : pinkishTheme.name;
    }, [
        locale
    ]);
    if (!ssr) {
        if (!setupDone) return null; // to prevent children that rely on some setup steps from firing their effects before the setup useEffect above
    }
    return /*#__PURE__*/ jsx(I18nProvider, _object_spread_props(_object_spread({}, locale), {
        children: /*#__PURE__*/ jsx(MemoizedMonitoringProvider, {
            monitoring: monitoring,
            children: /*#__PURE__*/ jsx(MemoizedInfraProvider, _object_spread_props(_object_spread({}, infra), {
                children: /*#__PURE__*/ jsx(MemoizedUserProvider, {
                    initialIdentifyContext: analytics.customerIoContext,
                    initialUser: user,
                    children: /*#__PURE__*/ jsx(MemoizedVisitorProvider, {
                        children: /*#__PURE__*/ jsx(MemoizedComplianceProvider, {
                            children: /*#__PURE__*/ jsx(MemoizedAnalyticsProvider, _object_spread_props(_object_spread({}, analytics), {
                                children: /*#__PURE__*/ jsx(MemoizedNavProvider, _object_spread_props(_object_spread({
                                    baseUrl: nav.monolithUrl
                                }, nav), {
                                    children: /*#__PURE__*/ jsx(MemoizedThemeProvider, {
                                        themeName: themeName,
                                        children: /*#__PURE__*/ jsx(MemoizedDialogProvider, {
                                            children: /*#__PURE__*/ jsx(MemoizedAuAProvider, _object_spread_props(_object_spread({}, layout === null || layout === void 0 ? void 0 : layout.aua), {
                                                children: /*#__PURE__*/ jsx(MemoizedLayoutProvider, {
                                                    auth: auth,
                                                    nav: nav,
                                                    children: /*#__PURE__*/ jsx(MemoizedRealtimeProvider, _object_spread_props(_object_spread({}, realtime), {
                                                        userId: user === null || user === void 0 ? void 0 : user.userId,
                                                        children: /*#__PURE__*/ jsx(MemoizedClientProvider, {
                                                            children: /*#__PURE__*/ jsxs(LemonadeAppUiContainer, _object_spread_props(_object_spread({}, props), {
                                                                children: [
                                                                    children,
                                                                    loginIframeState.showing ? /*#__PURE__*/ jsx(LoginIframe, {
                                                                        loginUrl: auth === null || auth === void 0 ? void 0 : auth.loginUrl,
                                                                        onSuccess: loginIframeState.callback,
                                                                        userInfoUrl: auth === null || auth === void 0 ? void 0 : auth.userInfoUrl
                                                                    }) : null
                                                                ]
                                                            }))
                                                        })
                                                    }))
                                                })
                                            }))
                                        })
                                    })
                                }))
                            }))
                        })
                    })
                })
            }))
        })
    }));
};

export { LemonadeApp };
